import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { signOut } from "firebase/auth";
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  getDoc, 
  setDoc,
  updateDoc, 
  arrayUnion, 
  arrayRemove 
} from "firebase/firestore";
import { auth, db } from '../firebase';
import { Order } from '../services/orderService';
import { Timestamp } from 'firebase/firestore';
import { ArrowLeft, LogOut, Package, MapPin, Plus, Trash2 } from 'lucide-react';

const PageContainer = styled.div`
  padding-top: var(--header-h);
  min-height: 100vh;
  background: #f5f5f5;
  width: 100%;
  padding-bottom: 60px;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: #333;
  font-size: 16px;
  cursor: pointer;
  margin-bottom: 30px;
  transition: color 0.3s ease;

  &:hover {
    color: #666;
  }
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 300;
  color: #333;
  margin: 0;
  letter-spacing: 1px;
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: 1px solid #ddd;
  color: #333;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;

  &:hover {
    background: #333;
    color: white;
    border-color: #333;
  }
`;

const WelcomeText = styled.p`
  color: #666;
  font-size: 16px;
  margin-bottom: 40px;
`;

const PhoneText = styled.p`
  color: #666;
  font-size: 14px;
  margin: 5px 0 0 0;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const Section = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  border: 1px solid #e0e0e0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const SectionTitle = styled.h2`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #e0e0e0;
`;

const EmptyState = styled.p`
  color: #999;
  font-size: 14px;
  text-align: center;
  padding: 40px 0;
`;

const OrderList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const OrderCard = styled.div`
  padding: 20px;
  background: #f9f9f9;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }
`;

const OrderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const OrderNumber = styled.div`
  font-weight: 600;
  color: #333;
  font-size: 16px;
`;

const OrderDate = styled.div`
  color: #666;
  font-size: 13px;
`;

const OrderDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
`;

const OrderDetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  color: #666;
`;

const StatusBadge = styled.span<{ status: string }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => 
    props.status === 'Teslim Edildi' ? '#d4edda' :
    props.status === 'İptal Edildi' ? '#f8d7da' :
    '#fff3cd'};
  color: ${props => 
    props.status === 'Teslim Edildi' ? '#155724' :
    props.status === 'İptal Edildi' ? '#721c24' :
    '#856404'};
`;

const AddressList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 20px;
`;

const AddressCard = styled.div`
  padding: 20px;
  background: #f9f9f9;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 15px;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`;

const AddressText = styled.p`
  flex: 1;
  margin: 0;
  color: #333;
  font-size: 14px;
  line-height: 1.6;
`;

const DeleteButton = styled.button`
  background: transparent;
  border: 1px solid #dc3545;
  color: #dc3545;
  padding: 6px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
  flex-shrink: 0;

  &:hover {
    background: #dc3545;
    color: white;
  }
`;

const ToggleButton = styled.button`
  width: 100%;
  background: #333;
  color: white;
  border: none;
  padding: 15px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;
  margin-top: 20px;

  &:hover {
    background: #555;
  }
`;

const AddressForm = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const AddressFieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 14px;
`;

const FieldGroup = styled.div<{ $full?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  ${props => props.$full ? 'grid-column: 1 / -1;' : ''}
`;

const FieldLabel = styled.label`
  font-size: 13px;
  color: #555;
  font-weight: 500;
`;

const FieldInput = styled.input`
  width: 100%;
  padding: 11px 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #1a1a1a;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  min-height: 100px;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #333;
  }
`;

const SaveButton = styled.button`
  background: #333;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  align-self: flex-start;

  &:hover {
    background: #555;
  }
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
`;

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const user = auth.currentUser;

  const [orders, setOrders] = useState<Order[]>([]);
  const [addresses, setAddresses] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [userPhone, setUserPhone] = useState<string>('');
  
  // Adres Ekleme/Düzenleme Modu
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  // Adres artik yapili alanlardan toplaniyor; Firestore'da yine tek metin
  // olarak saklaniyor ki mevcut kayitlar ve odeme akisi bozulmasin.
  const emptyAddressForm = {
    title: "",
    city: "",
    district: "",
    neighborhood: "",
    street: "",
    postalCode: "",
  };
  const [addressForm, setAddressForm] = useState(emptyAddressForm);

  const updateAddressField = (field: keyof typeof emptyAddressForm, value: string) => {
    setAddressForm(prev => ({ ...prev, [field]: value }));
  };

  const fetchUserData = useCallback(async () => {
    if (!user) return;

    try {
      // 1. Kullanıcının Siparişlerini Çek (customer.email ile)
      const ordersRef = collection(db, "orders");
      const q = query(ordersRef, where("customer.email", "==", user.email));
      const querySnapshot = await getDocs(q);
      
      const userOrders: Order[] = [];
      querySnapshot.forEach((docSnapshot) => {
        const data = docSnapshot.data();
        userOrders.push({
          id: docSnapshot.id,
          ...data,
          createdAt: data.createdAt && typeof data.createdAt.toDate === 'function' 
            ? data.createdAt.toDate() 
            : (data.createdAt instanceof Date ? data.createdAt : new Date(data.createdAt)),
          updatedAt: data.updatedAt && typeof data.updatedAt.toDate === 'function'
            ? data.updatedAt.toDate()
            : (data.updatedAt instanceof Date ? data.updatedAt : data.updatedAt ? new Date(data.updatedAt) : undefined),
        } as Order);
      });
      setOrders(userOrders);

      // 2. Kullanıcının Mevcut Adreslerini Çek
      const userDocRef = doc(db, "users", user.uid);
      const userDocSnap = await getDoc(userDocRef);
      
      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        console.log('📱 User data:', userData);
        console.log('📱 Phone from Firestore:', userData.phoneNumber, 'or', userData.phone);
        setAddresses(userData.addresses || []);
        const phone = userData.phoneNumber || userData.phone || '';
        console.log('📱 Setting phone to:', phone);
        setUserPhone(phone);
      } else {
        console.log('⚠️ User document does not exist');
      }

    } catch (error) {
      console.error("Veri çekme hatası:", error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchUserData();
    } else {
      setLoading(false);
    }
  }, [user, fetchUserData]);

  // Adres Ekleme Fonksiyonu
  const handleAddAddress = async () => {
    if (!user) return;

    const { title, city, district, neighborhood, street, postalCode } = addressForm;

    if (!city.trim() || !district.trim() || !neighborhood.trim() || !street.trim()) {
      alert("İl, ilçe, mahalle ve açık adres alanları zorunludur.");
      return;
    }

    if (postalCode.trim() && !/^[0-9]{5}$/.test(postalCode.trim())) {
      alert("Posta kodu 5 haneli olmalıdır.");
      return;
    }

    // Yapili alanlari tek satirlik okunabilir bir adrese cevir
    const parts = [
      street.trim(),
      `${neighborhood.trim()} Mah.`,
      `${district.trim()}/${city.trim()}`,
    ];
    let composed = parts.join(", ");
    if (postalCode.trim()) composed += ` ${postalCode.trim()}`;
    if (title.trim()) composed = `${title.trim()} - ${composed}`;

    try {
      const userRef = doc(db, "users", user.uid);
      const snapshot = await getDoc(userRef);

      if (!snapshot.exists()) {
        await setDoc(userRef, {
          addresses: [composed],
          email: user.email,
        });
      } else {
        await updateDoc(userRef, {
          addresses: arrayUnion(composed)
        });
      }

      setAddresses([...addresses, composed]);
      setAddressForm(emptyAddressForm);
      setIsEditingAddress(false);
      alert("Adres başarıyla eklendi!");
    } catch (error) {
      console.error("Adres ekleme hatası:", error);
      alert("Adres eklenirken hata oluştu.");
    }
  };

  // Adres Silme Fonksiyonu
  const handleDeleteAddress = async (addressToDelete: string) => {
    if (!window.confirm("Bu adresi silmek istediğinize emin misiniz?") || !user) return;

    try {
      const userDocRef = doc(db, "users", user.uid);
      await updateDoc(userDocRef, {
        addresses: arrayRemove(addressToDelete)
      });
      setAddresses(addresses.filter(addr => addr !== addressToDelete));
      alert("Adres başarıyla silindi!");
    } catch (error) {
      console.error("Adres silme hatası:", error);
      alert("Adres silinirken hata oluştu.");
    }
  };

  const handleLogout = () => {
    signOut(auth).then(() => {
      navigate('/');
    }).catch((error) => {
      console.error("Çıkış yapılırken hata:", error);
    });
  };

  if (loading) {
    return (
      <PageContainer>
        <Container>
          <LoadingContainer>Yükleniyor...</LoadingContainer>
        </Container>
      </PageContainer>
    );
  }

  if (!user) {
    return (
      <PageContainer>
        <Container>
          <BackButton onClick={() => navigate(-1)}>
            <ArrowLeft size={20} />
            Geri Dön
          </BackButton>
          <ErrorContainer>
            <h2>Giriş yapmanız gerekiyor</h2>
            <p>Profil sayfasını görüntülemek için lütfen giriş yapın.</p>
          </ErrorContainer>
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Container>
        <BackButton onClick={() => navigate(-1)}>
          <ArrowLeft size={20} />
          Geri Dön
        </BackButton>

        <HeaderSection>
          <div>
            <PageTitle>Hesabım</PageTitle>
            <WelcomeText>
              Hoş geldiniz, {user.displayName || user.email}
            </WelcomeText>
            {userPhone && (
              <PhoneText>
                📱 Telefon: {userPhone}
              </PhoneText>
            )}
          </div>
          <LogoutButton onClick={handleLogout}>
            <LogOut size={18} />
            Çıkış Yap
          </LogoutButton>
        </HeaderSection>

        <ContentGrid>
          {/* SOL KOLON: SİPARİŞLERİM */}
          <Section>
            <SectionTitle>
              <Package size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
              Siparişlerim
            </SectionTitle>
            
            {orders.length === 0 ? (
              <EmptyState>Henüz sipariş oluşturmadınız</EmptyState>
            ) : (
              <OrderList>
                {orders.map((order) => {
                  let orderDate: Date;
                  if (order.createdAt instanceof Date) {
                    orderDate = order.createdAt;
                  } else if (order.createdAt instanceof Timestamp) {
                    orderDate = order.createdAt.toDate();
                  } else {
                    orderDate = new Date((order.createdAt as any)?.seconds * 1000 || Date.now());
                  }

                  return (
                    <OrderCard key={order.id}>
                      <OrderHeader>
                        <OrderNumber>{order.orderNumber}</OrderNumber>
                        <OrderDate>{orderDate.toLocaleDateString('tr-TR')}</OrderDate>
                      </OrderHeader>
                      <OrderDetails>
                        <OrderDetailRow>
                          <span>Tutar:</span>
                          <strong>{order.totalAmount.toFixed(2)} TL</strong>
                        </OrderDetailRow>
                        <OrderDetailRow>
                          <span>Durum:</span>
                          <StatusBadge status={order.status}>{order.status}</StatusBadge>
                        </OrderDetailRow>
                        {order.items && order.items.length > 0 && (
                          <OrderDetailRow>
                            <span>Ürün Sayısı:</span>
                            <span>{order.items.length} ürün</span>
                          </OrderDetailRow>
                        )}
                      </OrderDetails>
                    </OrderCard>
                  );
                })}
              </OrderList>
            )}
          </Section>

          {/* SAĞ KOLON: ADRESLER */}
          <Section>
            <SectionTitle>
              <MapPin size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
              Kayıtlı Adreslerim
            </SectionTitle>

            {addresses.length > 0 && (
              <AddressList>
                {addresses.map((addr, index) => (
                  <AddressCard key={index}>
                    <AddressText>{addr}</AddressText>
                    <DeleteButton onClick={() => handleDeleteAddress(addr)}>
                      <Trash2 size={14} />
                      Sil
                    </DeleteButton>
                  </AddressCard>
                ))}
              </AddressList>
            )}

            {addresses.length === 0 && !isEditingAddress && (
              <EmptyState>Kayıtlı adresiniz bulunmamaktadır</EmptyState>
            )}

            <ToggleButton onClick={() => setIsEditingAddress(!isEditingAddress)}>
              {isEditingAddress ? (
                <>
                  <span>Kapat</span>
                </>
              ) : (
                <>
                  <Plus size={18} />
                  <span>Yeni Adres Ekle</span>
                </>
              )}
            </ToggleButton>

            {isEditingAddress && (
              <AddressForm>
                <AddressFieldGrid>
                  <FieldGroup $full>
                    <FieldLabel>Adres Başlığı (isteğe bağlı)</FieldLabel>
                    <FieldInput
                      value={addressForm.title}
                      onChange={(e) => updateAddressField("title", e.target.value)}
                      placeholder="Ev, İş..."
                    />
                  </FieldGroup>

                  <FieldGroup>
                    <FieldLabel>İl *</FieldLabel>
                    <FieldInput
                      value={addressForm.city}
                      onChange={(e) => updateAddressField("city", e.target.value)}
                      placeholder="Ankara"
                    />
                  </FieldGroup>

                  <FieldGroup>
                    <FieldLabel>İlçe *</FieldLabel>
                    <FieldInput
                      value={addressForm.district}
                      onChange={(e) => updateAddressField("district", e.target.value)}
                      placeholder="Çankaya"
                    />
                  </FieldGroup>

                  <FieldGroup>
                    <FieldLabel>Mahalle *</FieldLabel>
                    <FieldInput
                      value={addressForm.neighborhood}
                      onChange={(e) => updateAddressField("neighborhood", e.target.value)}
                      placeholder="Kültür"
                    />
                  </FieldGroup>

                  <FieldGroup>
                    <FieldLabel>Posta Kodu</FieldLabel>
                    <FieldInput
                      value={addressForm.postalCode}
                      onChange={(e) => updateAddressField("postalCode", e.target.value)}
                      placeholder="06100"
                      maxLength={5}
                      inputMode="numeric"
                    />
                  </FieldGroup>

                  <FieldGroup $full>
                    <FieldLabel>Açık Adres * (cadde, sokak, bina, daire)</FieldLabel>
                    <TextArea
                      value={addressForm.street}
                      onChange={(e) => updateAddressField("street", e.target.value)}
                      placeholder="Atatürk Cad. No:12 D:3"
                    />
                  </FieldGroup>
                </AddressFieldGrid>
                <SaveButton onClick={handleAddAddress}>
                  Adresi Kaydet
                </SaveButton>
              </AddressForm>
            )}
          </Section>
        </ContentGrid>
      </Container>
    </PageContainer>
  );
};

export default ProfilePage;
